import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormulairesService, Formulaire, FormulaireFile } from '../formulaires.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

@Component({
  selector: 'app-formulaires-detail',
  standalone: false,
  templateUrl: './formulaires-detail.component.html',
  styleUrls: ['./formulaires-detail.component.scss'],
})
export class FormulairesDetailComponent implements OnInit, OnDestroy {
  formulaire = signal<Formulaire | null>(null);
  isLoading = signal(true);
  isSaving = signal(false);
  errorMessage = signal<string | null>(null);

  editNomFr = signal('');
  editNomAr = signal('');
  editStatus = signal<'ACTI' | 'INAC'>('ACTI');

  // Fixed 2-slot file state (local until save)
  pdfFile = signal<FormulaireFile | null>(null);   // existing DB file
  docxFile = signal<FormulaireFile | null>(null);  // existing DB file
  pendingPdf = signal<File | null>(null);           // new file to upload
  pendingDocx = signal<File | null>(null);          // new file to upload
  clearedFileIds = signal<Set<number>>(new Set());  // to deactivate on save

  private destroy$ = new Subject<void>();
  private id = 0;

  constructor(
    private service: FormulairesService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) { this.id = +params['id']; this.loadFormulaire(); }
    });
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  private loadFormulaire(): void {
    this.isLoading.set(true);
    this.service.getFormulaireById(this.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const data: Formulaire = res.data;
        this.formulaire.set(data);
        this.editNomFr.set(data.formulaire_nom_fr || '');
        this.editNomAr.set(data.formulaire_nom_ar || '');
        this.editStatus.set((data.formulaire_status as 'ACTI' | 'INAC') || 'ACTI');
        this.pdfFile.set(data.files?.find(f => f.file_extension === 'PDF') ?? null);
        this.docxFile.set(data.files?.find(f => f.file_extension === 'DOCX') ?? null);
        this.pendingPdf.set(null);
        this.pendingDocx.set(null);
        this.clearedFileIds.set(new Set());
        this.isLoading.set(false);
      },
      error: () => { this.errorMessage.set('Erreur lors du chargement'); this.isLoading.set(false); }
    });
  }

  // ── File slot actions (local only) ─────────────────────────────────────────
  onReupload(type: 'pdf' | 'docx', event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== type) return;

    // Mark existing DB file for deactivation
    const existing = type === 'pdf' ? this.pdfFile() : this.docxFile();
    if (existing) {
      this.clearedFileIds.set(new Set([...this.clearedFileIds(), existing.file_id]));
      type === 'pdf' ? this.pdfFile.set(null) : this.docxFile.set(null);
    }

    type === 'pdf' ? this.pendingPdf.set(file) : this.pendingDocx.set(file);
    input.value = '';
  }

  clearFile(type: 'pdf' | 'docx'): void {
    const existing = type === 'pdf' ? this.pdfFile() : this.docxFile();
    if (existing) {
      this.clearedFileIds.set(new Set([...this.clearedFileIds(), existing.file_id]));
    }
    type === 'pdf' ? this.pdfFile.set(null) : this.docxFile.set(null);
    type === 'pdf' ? this.pendingPdf.set(null) : this.pendingDocx.set(null);
  }

  cancelPending(type: 'pdf' | 'docx'): void {
    // Restore the original DB file and remove pending
    const original = this.formulaire()?.files?.find(f => f.file_extension === (type === 'pdf' ? 'PDF' : 'DOCX')) ?? null;
    if (original) {
      // Remove from cleared set (restore)
      const cleared = new Set(this.clearedFileIds());
      cleared.delete(original.file_id);
      this.clearedFileIds.set(cleared);
      type === 'pdf' ? this.pdfFile.set(original) : this.docxFile.set(original);
    }
    type === 'pdf' ? this.pendingPdf.set(null) : this.pendingDocx.set(null);
  }

  // ── Save ───────────────────────────────────────────────────────────────────
  async saveChanges(): Promise<void> {
    if (!this.editNomFr().trim()) return;
    const confirmed = await SwalUtils.confirmGreen('Enregistrer', 'Voulez-vous enregistrer les modifications ?', 'Enregistrer');
    if (!confirmed.isConfirmed) return;

    this.isSaving.set(true);
    const steps: (() => Promise<void>)[] = [];

    steps.push(() => this.service.updateFormulaire(this.id, {
      formulaire_nom_fr: this.editNomFr().trim(),
      formulaire_nom_ar: this.editNomAr().trim(),
    }).toPromise().then(() => {}));

    if (this.editStatus() !== this.formulaire()?.formulaire_status) {
      steps.push(() => this.service.setStatus(this.id, this.editStatus()).toPromise().then(() => {}));
    }

    // Deactivate cleared files
    for (const fileId of this.clearedFileIds()) {
      steps.push(() => this.service.deleteFile(this.id, fileId).toPromise().then(() => {}));
    }

    // Upload pending files
    const pending: File[] = [];
    if (this.pendingPdf()) pending.push(this.pendingPdf()!);
    if (this.pendingDocx()) pending.push(this.pendingDocx()!);
    if (pending.length) {
      const form = new FormData();
      pending.forEach(f => form.append('files', f));
      steps.push(() => this.service.addFiles(this.id, form).toPromise().then(() => {}));
    }

    try {
      for (const step of steps) await step();
      this.isSaving.set(false);
      this.loadFormulaire();
      SwalUtils.success('Enregistré', 'Le formulaire a été mis à jour avec succès.');
    } catch {
      this.isSaving.set(false);
    }
  }

  onBack(): void { this.router.navigate(['/federation/formulaires']); }
}
